export interface NotificationExtraField {
  key: string;
  label: string;
  placeholder?: string;
  type?: "text" | "email";
}

export interface NotificationChannelDef {
  label: string;
  actionId: string;
  connectorType: string | null;
  iconUrl: string;
  mapPayload: (message: string, destination: string, extras?: Record<string, string>) => Record<string, unknown>;
  // destinationInput is null for connector-only channels (e.g. Discord via webhook connector).
  // When null, the connector selection itself provides the destination — no separate input.
  destinationInput: { type: string; label: string } | null;
  extraFields?: NotificationExtraField[];
}

export interface NotificationConfig {
  channel: string;
  actionId: string;
  connectorType: string | null;
  connectorId: string | null;
  destination: string;
  destinationLabel: string;
  extras?: Record<string, string>;
}

// Draft state captured while the user is still picking a channel. Persisted in
// the virtual node's payload as `$draft*` fields so the picker rehydrates when
// the sidebar is closed and reopened mid-configuration.
export interface NotificationDraft {
  channel: string | null;
  connectorId: string | null;
  destination: string;
  destinationLabel: string;
  extras: Record<string, string>;
}

const iconUrl = (brand: string) => `https://ai-icon.b3.fun/logo/${encodeURIComponent(brand)}`;

export const NOTIFICATION_CHANNELS: Record<string, NotificationChannelDef> = {
  log: {
    label: "Log Only",
    actionId: "log",
    connectorType: null,
    iconUrl: iconUrl("terminal"),
    mapPayload: message => ({ message }),
    destinationInput: null,
  },
  slack: {
    label: "Slack",
    actionId: "slack-send-message",
    connectorType: "slack",
    iconUrl: iconUrl("slack"),
    mapPayload: (message, destination) => ({ text: message, channel: destination }),
    destinationInput: { type: "slack-channel", label: "Channel" },
  },
  telegram: {
    label: "Telegram",
    actionId: "send-telegram-message",
    connectorType: "telegram-bot",
    iconUrl: iconUrl("telegram"),
    mapPayload: (message, destination) => ({ text: message, chatId: destination }),
    destinationInput: { type: "telegram-chat", label: "Chat" },
  },
  discord: {
    label: "Discord",
    actionId: "send-discord-message",
    connectorType: "discord",
    iconUrl: iconUrl("discord"),
    // Discord connector provides webhookUrl at runtime (merged into inputs by the
    // worker). Schema is strict additionalProperties:false with only `message`,
    // so the payload must contain ONLY message.
    mapPayload: message => ({ message }),
    destinationInput: null,
  },
  email: {
    label: "Email",
    actionId: "send-email",
    connectorType: null,
    iconUrl: iconUrl("email"),
    mapPayload: (message, destination) => ({ text: message, to: destination, subject: "Notification" }),
    destinationInput: { type: "email", label: "Email Address" },
  },
  sms: {
    label: "SMS",
    actionId: "twilio-send-sms",
    connectorType: "pipedream:twilio",
    iconUrl: iconUrl("twilio"),
    // `from` is a required Twilio phone number and is not stored on the
    // pipedream connector (which only keeps the Pipedream account ID). Collect
    // it via an extra field so the resolved payload satisfies the strict
    // twilio-send-sms schema (required: to, body, from).
    mapPayload: (message, destination, extras) => ({
      body: message,
      to: destination,
      from: extras?.from ?? "",
    }),
    destinationInput: { type: "text", label: "Recipient Phone Number" },
    extraFields: [
      {
        key: "from",
        label: "Sender Phone Number",
        placeholder: "+16175551212",
      },
    ],
  },
  pushover: {
    label: "Pushover",
    actionId: "send-pushover-notification",
    connectorType: "pushover",
    iconUrl: iconUrl("pushover"),
    mapPayload: (message, destination) => ({ userKey: destination, message }),
    destinationInput: { type: "pushover-recipient", label: "Recipient" },
  },
  pushbullet: {
    label: "Pushbullet",
    actionId: "send-pushbullet-push",
    connectorType: "pushbullet",
    iconUrl: iconUrl("pushbullet"),
    // Pushbullet requires title + body. Default title to "Notification" so the
    // notification picker stays a single-field input; users can override the
    // title later by promoting the action node from the virtual notification.
    mapPayload: (message, destination) => ({
      deviceIden: destination,
      type: "note",
      title: "Notification",
      body: message,
    }),
    destinationInput: { type: "pushbullet-device", label: "Device (empty = all)" },
  },
  webpush: {
    label: "Web Push",
    actionId: "send-web-push",
    connectorType: "webpush",
    iconUrl: iconUrl("webpush"),
    mapPayload: (message, destination) => ({
      subscriptionId: destination,
      title: "Notification",
      body: message,
    }),
    destinationInput: { type: "webpush-subscription", label: "Subscribed browser" },
  },
};

// Action IDs that the migration converts to the virtual notification type and
// that `detectNotificationNodes` treats as conversion candidates. Keep in sync
// with services/b3os-workflow/migrations/000185_convert_template_notification_nodes.up.sql.
// Intentionally excludes slack-send-message-to-user-or-group: DM semantics
// differ from channel post, and mapping DM → channel would silently change
// the destination. If DM support in the picker is needed, add a dedicated
// slackDM channel in its own change.
export const NOTIFICATION_ACTION_IDS = new Set([
  "slack-send-message",
  "send-telegram-message",
  "send-discord-message",
  "send-email",
  "gmail-send-email",
  "email-send-email",
  "twilio-send-sms",
  "send-pushover-notification",
  "send-pushbullet-push",
  "send-web-push",
]);

export const MESSAGE_FIELD_MAP: Record<string, string> = {
  log: "message",
  "slack-send-message": "text",
  "send-telegram-message": "text",
  "send-discord-message": "message",
  "send-email": "text",
  "gmail-send-email": "body",
  "email-send-email": "text",
  "twilio-send-sms": "body",
  "send-pushover-notification": "message",
  "send-pushbullet-push": "body",
  "send-web-push": "body",
};

export const NOTIFICATION_NODE_TYPE = "notification";
export const NOTIFY_NODE_BASE = "notify";
export const NOTIFICATION_PROP_KEY = "$notification";
export const GOOGLE_SHEETS_NODE_PREFIX = "google-sheets-";

export function isGoogleSheetsConnectorType(type: string): boolean {
  return type === "pipedream:google_sheets";
}

export function isGoogleSheetsNodeType(type: string): boolean {
  return type.startsWith(GOOGLE_SHEETS_NODE_PREFIX);
}
export const GOOGLE_SHEET_TEMPLATE_URL_PROP_KEY = "$google_sheet_template_url";

// Keys used to persist intermediate picker state on a virtual notification
// node's payload. Exported so sidebar/picker can read/write consistently and
// so resolve/convert helpers can strip them when transitioning to concrete
// actions.
export const NOTIFICATION_DRAFT_KEYS = {
  channel: "$draftChannel",
  connectorId: "$draftConnectorId",
  destination: "$draftDestination",
  destinationLabel: "$draftDestinationLabel",
  extras: "$draftExtras",
} as const;
